import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken } from '@angular/core';
import type { MultiLanguageChronicleItem } from '@check24/chronicle-api';
import { environment } from '../../../environments/environment';
import { Result } from '../type/result_type';

declare global {
  interface Window {
    wf_sso_type?: string;
  }
}

export type ChronicleUpsert = (itemId: string, item: MultiLanguageChronicleItem) => Promise<void>;

export const CHRONICLE_UPSERT = new InjectionToken<ChronicleUpsert>('CHRONICLE_UPSERT');

const PRIMARY_IMAGE_URL = 'https://www.check24.at/uploads/App_Home_Teaser_Oesterreich_Kredit_1200px_c80d7c930c.jpg';

/** Writes the guest's comparison card to Chronicle. The page works the same without it. */
@Injectable({ providedIn: 'root' })
export class ChronicleService {
  private readonly upsert = inject(CHRONICLE_UPSERT, { optional: true });
  private readonly document = inject(DOCUMENT);
  private readonly attempted = new Set<string>();

  async startComparison({ creditApplicationId, loan }: Pick<Result, 'creditApplicationId' | 'loan'>) {
    const resumeUrl = this.resumeUrl();
    if (
      !this.upsert ||
      !creditApplicationId ||
      !resumeUrl ||
      !this.isGuest() ||
      this.attempted.has(creditApplicationId)
    ) {
      return;
    }
    this.attempted.add(creditApplicationId);
    try {
      await this.upsert(creditApplicationId, toItem(loan, resumeUrl));
    } catch {
      // ponytail: a failed write is dropped on purpose, without retry or reporting. The next page load tries again.
    }
  }

  // The page's own link, reduced to its result token. Chronicle only accepts https.
  private resumeUrl() {
    const { protocol, origin, pathname, searchParams } = new URL(this.document.location.href);
    const token = searchParams.get('t');
    return protocol === 'https:' && token ? `${origin}${pathname}?t=${encodeURIComponent(token)}` : undefined;
  }

  // Only a confirmed guest writes. Logged in, expired and unknown sessions all skip.
  private isGuest() {
    return environment.chronicleGuestWrites && window.wf_sso_type === 'GUEST';
  }
}

// Only AT is served, but the SDK type requires every language.
const translated = (text: string) => ({ 'de-AT': text, 'de-DE': text, 'es-ES': text });

function toItem({ value, duration }: Result['loan'], resumeUrl: string) {
  return {
    product_slug: 'atfinkre',
    title: translated('Kredit-Vergleich fortsetzen'),
    primary_line: translated(`${value} €`),
    secondary_line: { text: translated(`${duration} Monate`), style: 'bold' },
    primary_image: { url: PRIMARY_IMAGE_URL, mode: 'cover' },
    target_url: resumeUrl,
  } satisfies MultiLanguageChronicleItem;
}
